/** Dialer / Comms page (v1 dialer-comms.js). Left: conversation threads. Right:
 * the selected SMS/call thread with bubbles, a compose box, and click-to-call.
 * Reads (threads/status) run against the live Quo backend and are safe; Send and
 * Call are gated by liveFire (quoSend / quoCall) — disabled with a note until the
 * supervised go-live, exactly like the rest of the beta's real-fire actions. */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchQuoStatus, fetchComms, fetchQuoNumbers, sendSms, placeCall, groupThreads, allLines, commTime, callDuration, commLine,
  renderSmsTemplate, loadSmsTemplates, saveSmsTemplates, type SmsTemplate,
  type QuoStatus, type QuoNumber, type CommRow, type Thread,
} from "./api";
import { isPersonName } from "../inbox/api";
import type { Contact } from "../contacts/api";
import { usePhone } from "../../lib/usePhone";
import { BackendError } from "../../lib/backend";
import { useConfirmDelete } from "../common/ConfirmDelete";
import { newIdemKey } from "../../lib/idem";

/** How many of the MOST RECENT comms to pull. The backend used to serve these
 * oldest-first, so this number silently became "how far back the Inbox is
 * frozen" instead of "how much history you get" — see routes/comms.py. With
 * that fixed, 2000 covers roughly the last half-year at Tom's volume. Note
 * the search box only matches what's loaded, so raising this widens search. */
const COMMS_LIMIT = 2000;

/** last-10 digits — the phone key used to match a thread to a CRM contact. */
const last10 = (v: string | null): string => String(v ?? "").replace(/[^0-9]/g, "").slice(-10);
/** Pretty-print a business line for the filter (falls back to the raw value). */
const fmtLine = (n: string): string => {
  const d = n.replace(/[^0-9]/g, "");
  const ten = d.length > 10 ? d.slice(-10) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : (n || "Unknown line");
};

export function DialerPage({
  token,
  orgId,
  canSend,
  canCall,
  onWake,
  findLeadAddress,
  contacts,
  onOpenLead,
  openPeer,
  onOpenedPeer,
}: {
  token: string | null;
  orgId: string;
  canSend: boolean;
  canCall: boolean;
  onWake: () => void;
  /** link a thread to its property (merged from the old Inbox) */
  findLeadAddress?: (t: Thread) => string | null;
  /** CRM contacts — resolve a thread's name and let search match them */
  contacts?: Contact[];
  /** jump to a linked lead/property */
  onOpenLead?: (address: string) => void;
  /** cross-page jump: open this contact number's thread (Hot page → comms) */
  openPeer?: string | null;
  onOpenedPeer?: () => void;
}) {
  const [status, setStatus] = useState<QuoStatus | null>(null);
  const [comms, setComms] = useState<CommRow[] | null>(null);
  /** Chats | Calls — Quo splits these and so do we. Calls were only ever
   * visible buried inside a thread, so a missed call from someone who has
   * never texted was effectively invisible. */
  const [tab, setTab] = useState<"chats" | "calls">("chats");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selNumber, setSelNumber] = useState<string | null>(null);
  // "new conversation": start a thread with a typed number or a picked contact,
  // even if there's no message history yet (synthesizes an empty thread below).
  const [newOpen, setNewOpen] = useState(false);
  const [newInput, setNewInput] = useState("");
  const [newConvo, setNewConvo] = useState<{ number: string; name: string } | null>(null);
  const [q, setQ] = useState("");
  const [lineFilter, setLineFilter] = useState<string>("all"); // which of my Quo lines
  const [quoNums, setQuoNums] = useState<QuoNumber[]>([]); // for line labels
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [tplMgr, setTplMgr] = useState(false);
  const [templates, setTemplates] = useState<SmsTemplate[]>(() => loadSmsTemplates());
  const onSaveTemplates = (list: SmsTemplate[]) => { setTemplates(list); saveSmsTemplates(list); };
  const confirmDel = useConfirmDelete(); // v1 dpDelTemplate — typed-delete gated
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const idemRef = useRef<string | null>(null); // per-draft send key (idempotency)
  /** Phone = drill-in navigation: the list is a screen, a thread is a screen.
   *  The desktop two-pane grid stacked vertically gave 188 chats a 3-row
   *  window above an empty "Pick a conversation." pane (Tom, 2026-07-30). */
  const isPhone = usePhone();

  const load = () => {
    setLoading(true);
    setErr(null);
    onWake(); // surface the "waking the backend…" hint during Render cold-start
    void Promise.all([
      fetchQuoStatus(token, undefined).catch(() => null),
      fetchComms(token, { limit: COMMS_LIMIT }, undefined),
      fetchQuoNumbers(token, undefined).catch(() => [] as QuoNumber[]),
    ])
      .then(([st, rows, nums]) => { setStatus(st); setComms(rows); setQuoNums(nums); })
      .catch((e) => setErr(e instanceof BackendError ? e.detail : "Could not reach the backend"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // cross-page jump (Hot page → this thread), merged from the old Inbox
  useEffect(() => {
    if (!openPeer) return;
    setSelNumber(openPeer);
    onOpenedPeer?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPeer]);

  const threads = useMemo(() => (comms ? groupThreads(comms) : []), [comms]);
  // Filter options = every business line seen in messages UNION your Quo numbers
  // (so a number with no messages yet still shows), deduped by last-10 digits.
  const lines = useMemo(() => {
    const seen = new Map<string, string>();
    for (const ln of allLines(threads)) { const k = last10(ln); if (k && !seen.has(k)) seen.set(k, ln); }
    for (const u of quoNums) for (const n of u.numbers) { const k = last10(n); if (k && !seen.has(k)) seen.set(k, n); }
    return [...seen.values()];
  }, [threads, quoNums]);
  // CRM contact name by phone (last-10 digits) — resolves thread names and
  // lets the search box match a name that only exists in the CRM, not in Quo.
  const contactByPhone = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of contacts ?? []) {
      for (const p of [c.phone, c.phoneWork, c.phoneHome]) {
        const k = last10(p);
        if (k && !m.has(k)) m.set(k, c.name);
      }
    }
    return m;
  }, [contacts]);
  const lineLabel = (ln: string): string => {
    const hit = quoNums.find((u) => u.numbers.some((x) => last10(x) === last10(ln)));
    return hit && hit.name && last10(hit.name) !== last10(ln) ? `${hit.name} · ${fmtLine(ln)}` : fmtLine(ln);
  };
  // isPersonName filters out the PROPERTY ADDRESSES that land in contact_name
  // when we text about a lead — otherwise threads show up titled
  // "4732 W Bay Vista Ave, Tampa, FL 33611" instead of who you're talking to.
  const threadName = (t: Thread): string =>
    (isPersonName(t.name) && t.name !== t.number ? t.name : "")
    || contactByPhone.get(last10(t.number))
    || fmtLine(t.number);
  const shownThreads = useMemo(() => {
    const n = q.trim().toLowerCase();
    return threads.filter((t) => {
      if (lineFilter !== "all" && !t.lines.some((l) => last10(l) === last10(lineFilter))) return false;
      if (!n) return true;
      const addr = findLeadAddress?.(t) ?? "";
      const crmName = contactByPhone.get(last10(t.number)) ?? "";
      return (t.name + " " + t.number + " " + t.last.body + " " + addr + " " + crmName).toLowerCase().includes(n);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, q, lineFilter, findLeadAddress, contactByPhone]);

  /** The Calls tab — a flat call log, newest first, obeying the same line
   * filter and search box as Chats. Calls live in the same `comms` table as
   * texts; before this they only appeared as one line inside a thread, so a
   * missed call from a number that has never texted you was invisible. */
  const shownCalls = useMemo(() => {
    const n = q.trim().toLowerCase();
    return (comms ?? [])
      .filter((c) => c.channel === "call")
      .filter((c) => lineFilter === "all" || last10(commLine(c)) === last10(lineFilter))
      .filter((c) => {
        if (!n) return true;
        const crmName = contactByPhone.get(last10(c.contactNumber)) ?? "";
        return (c.contactName + " " + c.contactNumber + " " + crmName).toLowerCase().includes(n);
      })
      .sort((a, b) => b.createdAt - a.createdAt);
  }, [comms, q, lineFilter, contactByPhone]);
  const callName = (c: CommRow): string =>
    (isPersonName(c.contactName) && c.contactName !== c.contactNumber ? c.contactName : "")
    || contactByPhone.get(last10(c.contactNumber))
    || fmtLine(c.contactNumber);
  /** Quo's own vocabulary: an inbound call with no duration was never picked
   * up. Anything else is a completed call in one direction or the other. */
  const callFace = (c: CommRow): { icon: string; label: string; missed: boolean } => {
    const missed = c.direction === "in" && !(c.duration && c.duration > 0);
    if (missed) return { icon: "↙", label: "Missed call", missed: true };
    if (c.direction === "in") return { icon: "↙", label: "You answered", missed: false };
    return { icon: "↗", label: "You called", missed: false };
  };
  // real thread if we have history, else the synthetic empty thread for a brand-new
  // conversation (so the composer renders and a first text can go out).
  const sel: Thread | null = selNumber
    ? threads.find((t) => t.number === selNumber)
      ?? (newConvo && newConvo.number === selNumber
        ? {
            number: newConvo.number,
            name: newConvo.name || newConvo.number,
            messages: [],
            last: { id: "new:" + newConvo.number, direction: "out", channel: "sms", contactNumber: newConvo.number, contactName: newConvo.name, body: "", status: "", createdAt: Date.now(), duration: null, leadKey: null, fromNumber: "", toNumber: newConvo.number } as CommRow,
            unreadIn: 0,
            lines: [],
          }
        : null)
    : null;
  // start a new conversation — need a real (10+ digit) number before we target a send
  const startNewConvo = (rawNumber: string, name: string) => {
    const digits = rawNumber.replace(/[^\d+]/g, "");
    if (digits.replace(/\D/g, "").length < 10) return;
    setNewConvo({ number: digits, name });
    setSelNumber(digits);
    setNewOpen(false);
    setNewInput("");
  };
  // contacts (with a phone) matching the new-conversation search
  const newMatches = useMemo(() => {
    const nq = newInput.trim().toLowerCase();
    const seen = new Set<string>();
    const out: { name: string; phone: string }[] = [];
    for (const c of contacts ?? []) {
      const phone = [c.phone, c.phoneWork, c.phoneHome].find((p) => last10(p).length === 10) ?? "";
      if (!phone) continue;
      const k = last10(phone);
      if (seen.has(k)) continue;
      if (nq && !(c.name + " " + phone).toLowerCase().includes(nq)) continue;
      seen.add(k);
      out.push({ name: c.name, phone });
      if (out.length >= 30) break;
    }
    return out;
  }, [contacts, newInput]);
  const newInputIsNumber = newInput.replace(/\D/g, "").length >= 10;
  const selAddr = sel ? findLeadAddress?.(sel) ?? null : null;

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: "end" }); }, [sel]);

  const send = async () => {
    const text = draft.trim();
    if (!text || !sel || !canSend) return;
    setBusy(true); setNote(null);
    // idempotency: mint one key per draft at first attempt; a retry after a
    // timeout re-sends the SAME key so the server delivers exactly once.
    if (!idemRef.current) idemRef.current = newIdemKey();
    try {
      await sendSms(token, { org_id: orgId, to: sel.number, text, idem_key: idemRef.current, ...(sel.name && sel.name !== sel.number ? { contact_name: sel.name } : {}), ...(sel.last.leadKey ? { lead_key: sel.last.leadKey } : {}) });
      idemRef.current = null;   // confirmed — next draft gets a fresh key
      setDraft("");
      setNote("Sent ✓");
      // reflect immediately, then re-pull the authoritative thread
      setComms((prev) => (prev ? [...prev, { id: "local:" + Date.now(), direction: "out", channel: "sms", contactNumber: sel.number, contactName: sel.name, body: text, status: "sent", createdAt: Date.now(), duration: null, leadKey: sel.last.leadKey, fromNumber: lineFilter !== "all" ? lineFilter : (sel.lines[0] ?? ""), toNumber: sel.number } as CommRow] : prev));
      void fetchComms(token, { limit: COMMS_LIMIT }).then(setComms).catch(() => {});
    } catch (e) {
      setNote(e instanceof BackendError ? "Send failed: " + e.detail : "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const call = async () => {
    if (!sel || !canCall) return;
    setBusy(true); setNote(null);
    try {
      await placeCall(token, { org_id: orgId, to: sel.number, ...(sel.name && sel.name !== sel.number ? { contact_name: sel.name } : {}) });
      setNote("Calling " + sel.name + "…");
    } catch (e) {
      setNote(e instanceof BackendError ? "Call failed: " + e.detail : "Call failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="leads dialer">
      <div className="exphead">
        <h1>Inbox</h1>
        <div className="expacts">
          <button
            className={"btn slim primary" + (isPhone ? " iconbtn" : "")}
            aria-label={newOpen ? "Cancel new conversation" : "New conversation"}
            onClick={() => { setNewOpen((v) => !v); setNewInput(""); }}
          >{isPhone ? (newOpen ? "✕" : "＋") : newOpen ? "Cancel" : "＋ New conversation"}</button>
          <button
            className={"btn slim" + (isPhone ? " iconbtn" : "")}
            aria-label="Refresh"
            onClick={load}
            disabled={loading}
          >{isPhone ? (loading ? "…" : "↻") : loading ? "Loading…" : "Refresh"}</button>
        </div>
      </div>
      {/* The all-good explainer is onboarding text charging rent — hidden on
          phones. The two WARNING variants (no API key / sending off) carry
          real information and stay everywhere. */}
      {isPhone && status?.apiKeySet && canSend ? null : (
      <div className="expqbo">
        {status && !status.apiKeySet
          ? "Quo isn't configured on the backend yet (no API key) — threads will be empty until it's connected."
          : canSend
            ? "SMS + calls over your Quo line — reading, texting, and calling are live."
            : "SMS + calls over your Quo line. Reading is live; sending a text or placing a call is off on this beta."}
      </div>
      )}

      {err ? <div className="inbxnote err">{err}</div> : null}

      <div className={"dialerwrap" + (isPhone ? " drill" : "")}>
        {/* phone: exactly one screen at a time — list, or the open thread */}
        {isPhone && sel ? null : (
        <div className="dialerlist">
          {newOpen ? (
            <div className="dialernew">
              <input className="dialersearch" autoFocus placeholder="Type a phone number, or search contacts…" value={newInput} onChange={(e) => setNewInput(e.target.value)} />
              {newInputIsNumber ? (
                <button className="dialernewstart" onClick={() => startNewConvo(newInput, "")}>＋ Start chat with {fmtLine(newInput)}</button>
              ) : null}
              <div className="dialernewlist">
                {newMatches.map((c) => (
                  <button key={c.phone} className="dialernewcontact" onClick={() => startNewConvo(c.phone, c.name)}>
                    <strong>{c.name || "Contact"}</strong><span className="dim">{fmtLine(c.phone)}</span>
                  </button>
                ))}
                {newMatches.length === 0 && !newInputIsNumber ? <div className="expempty">Type a full number, or search your contacts by name.</div> : null}
              </div>
            </div>
          ) : null}
          {/* desktop: the line filter is its own row. Phone: it rides beside
              the tabs as a small pill — one row of chrome instead of two. */}
          {!isPhone && lines.length > 1 ? (
            <select className="dialerline" value={lineFilter} onChange={(e) => setLineFilter(e.target.value)} title="Show messages on one of your numbers">
              <option value="all">All my numbers ({lines.length})</option>
              {lines.map((ln) => <option key={ln} value={ln}>{lineLabel(ln)}</option>)}
            </select>
          ) : null}
          <div className="dialertabs" role="tablist">
            <button role="tab" aria-selected={tab === "chats"} className={tab === "chats" ? "on" : ""} onClick={() => setTab("chats")}>
              Chats {comms ? <span className="dtabn">{shownThreads.length}</span> : null}
            </button>
            <button role="tab" aria-selected={tab === "calls"} className={tab === "calls" ? "on" : ""} onClick={() => setTab("calls")}>
              Calls {comms ? <span className="dtabn">{shownCalls.length}</span> : null}
            </button>
            {isPhone && lines.length > 1 ? (
              <select className="dialerline mini" value={lineFilter} onChange={(e) => setLineFilter(e.target.value)} title="Show messages on one of your numbers" aria-label="Filter by line">
                <option value="all">{lines.length} lines</option>
                {lines.map((ln) => <option key={ln} value={ln}>{lineLabel(ln)}</option>)}
              </select>
            ) : null}
          </div>
          <input className="dialersearch" placeholder={tab === "calls" ? "Search calls by name or number…" : "Search name, number, contact or address…"} value={q} onChange={(e) => setQ(e.target.value)} />
          {loading && !comms ? (
            <div className="skelstack" aria-hidden>
              <div className="skel" style={{ height: 52 }} />
              <div className="skel" style={{ height: 52 }} />
              <div className="skel" style={{ height: 52 }} />
              <div className="skel" style={{ height: 52, opacity: 0.6 }} />
            </div>
          ) : null}
          {tab === "calls" ? (
            <>
              {comms && shownCalls.length === 0 ? (
                <div className="expempty">{q.trim() ? "No matching calls." : "No calls yet."}</div>
              ) : null}
              {shownCalls.map((c) => {
                const f = callFace(c);
                const dur = callDuration(c.duration);
                return (
                  <button
                    key={c.id}
                    className={"dialercall" + (c.contactNumber === selNumber ? " on" : "") + (f.missed ? " missed" : "")}
                    // opening the thread is the useful next action after seeing
                    // a missed call — you can text them back immediately
                    onClick={() => { setSelNumber(c.contactNumber); setTab("chats"); }}
                    title="Open this conversation"
                  >
                    {isPhone ? <span className={"davat call" + (f.missed ? " missed" : "")}>📞</span> : null}
                    <span className="dthreadmain">
                    <div className="dthreadtop">
                      <strong>{callName(c)}</strong>
                      <span className="dim">{commTime(c.createdAt)}</span>
                    </div>
                    <div className="dthreadlast">
                      <span className="dcalldir">{f.icon}</span> {f.label}
                      {dur ? <span className="dim"> · {dur}</span> : null}
                    </div>
                    </span>
                  </button>
                );
              })}
            </>
          ) : null}
          {tab === "chats" && comms && shownThreads.length === 0 ? <div className="expempty">{threads.length ? "No matches." : "No conversations yet."}</div> : null}
          {(tab === "chats" ? shownThreads : []).map((t) => {
            const addr = findLeadAddress?.(t) ?? null;
            const nm = threadName(t);
            const initials = nm.split(/\s+/).map((s) => s[0] ?? "").join("").slice(0, 2).toUpperCase() || "☎";
            return (
              <button key={t.number} className={"dialerthread" + (t.number === selNumber ? " on" : "")} onClick={() => setSelNumber(t.number)}>
                {/* the avatar is phone-only: it earns its 34px by making 12
                    rows scannable at walking pace; desktop rows are fine */}
                {isPhone ? <span className="davat">{/^[\d(]/.test(nm) ? "☎" : initials}</span> : null}
                <span className="dthreadmain">
                <div className="dthreadtop">
                  <strong>{nm}</strong>
                  <span className="dim">{commTime(t.last.createdAt)}</span>
                </div>
                <div className="dthreadlast">
                  {t.last.channel === "call" ? "📞 " : t.last.direction === "out" ? "↗ " : "↘ "}
                  {t.last.body || (t.last.channel === "call" ? "Call" : "")}
                  {isPhone && addr ? <span className="dim"> · 🏠 {addr}</span> : null}
                </div>
                {!isPhone && addr ? <div className="dthreadaddr">🏠 {addr}</div> : null}
                </span>
              </button>
            );
          })}
        </div>
        )}

        {isPhone && !sel ? null : (
        <div className="dialerconvo">
          {!sel ? (
            <div className="expempty">{comms ? "Pick a conversation." : ""}</div>
          ) : (
            <>
              <div className="dconvohead">
                {isPhone ? (
                  <button className="dback" aria-label="Back to inbox" onClick={() => { setSelNumber(null); setNewConvo(null); }}>‹ Inbox</button>
                ) : null}
                <div>
                  <strong>{threadName(sel)}</strong>
                  {threadName(sel) !== sel.number ? <span className="dim"> · {sel.number}</span> : null}
                  {selAddr ? <button className="dconvoaddr" onClick={() => onOpenLead?.(selAddr)} title="Open this property">🏠 {selAddr}</button> : null}
                </div>
                <button className="btn slim" disabled={!canCall || busy} title={canCall ? "Call" : "Calling is off on this beta"} onClick={call}>📞 Call</button>
              </div>
              <div className="dconvobody">
                {sel.messages.map((m) => (
                  <div key={m.id} className={"dmsg " + m.direction + (m.channel === "call" ? " call" : "")}>
                    <div className="dbubble">
                      {m.channel === "call"
                        ? <span>📞 {m.direction === "out" ? "Outbound" : "Inbound"} call{m.duration ? " · " + callDuration(m.duration) : ""}</span>
                        : m.body}
                    </div>
                    <div className="dmeta">{commTime(m.createdAt)}{m.status && m.direction === "out" ? " · " + m.status : ""}</div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {note ? <div className="dnote">{note}</div> : null}
              <div className="dtplbar">
                {templates.length ? (
                  <select
                    className="dtplpick"
                    value=""
                    onChange={(e) => {
                      const t = templates[Number(e.target.value)];
                      if (t) setDraft((d) => (d.trim() ? d.trimEnd() + " " : "") + renderSmsTemplate(t.text, { name: sel.name }));
                      e.currentTarget.value = "";
                    }}
                  >
                    <option value="">Insert template…</option>
                    {templates.map((t, i) => <option key={i} value={i}>{t.label}</option>)}
                  </select>
                ) : <span className="sub">No templates yet.</span>}
                {draft.trim() ? (
                  <button className="btn slim" onClick={() => { onSaveTemplates([...templates, { label: draft.trim().slice(0, 28), text: draft.trim() }]); }}>+ Save as template</button>
                ) : null}
                <button className="btn slim" onClick={() => setTplMgr((v) => !v)}>{tplMgr ? "Done" : "Manage"}</button>
              </div>
              {tplMgr ? (
                <div className="dtplmgr">
                  {templates.length === 0 ? <span className="sub">Add a template by typing a message, then “Save as template”.</span> : null}
                  {templates.map((t, i) => (
                    <div key={i} className="dtplrow">
                      <span className="dtpllbl">{t.label}</span>
                      <span className="sub dtpltext">{t.text}</span>
                      <button className="rowbtn del" title="Remove" onClick={() => confirmDel.ask("Delete this template?", () => onSaveTemplates(templates.filter((_, k) => k !== i)))}>✕</button>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="dcompose">
                <textarea
                  rows={2}
                  placeholder={canSend ? "Type a message…" : "Sending is off on this beta"}
                  value={draft}
                  disabled={!canSend || busy}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
                />
                <button className="btn slim primary" disabled={!canSend || busy || !draft.trim()} title={canSend ? "Send" : "Sending is off on this beta"} onClick={send}>
                  {busy ? "…" : "Send"}
                </button>
              </div>
            </>
          )}
        </div>
        )}
      </div>
      {confirmDel.modal}
    </div>
  );
}
